import {
  Button,
  DatePicker,
  Form,
  Input,
  Modal,
  Select,
  UploadFile,
} from "antd";
import { ReactNode, useEffect, useState } from "react";
import styled from "styled-components";
import { useAtomValue } from "jotai";
import { debounceTime, Subject } from "rxjs";
import _ from "lodash";
import { v4 } from "uuid";
import {
  MdAccessTime,
  MdAdd,
  MdAspectRatio,
  MdBusinessCenter,
  MdClose,
  MdContentCopy,
  MdEvent,
  MdFormatQuote,
  MdGroups,
  MdHandyman,
  MdImage,
  MdInfoOutline,
  MdMovieCreation,
  MdPersonOutline,
  MdPhotoLibrary,
  MdSubject,
  MdTitle,
  MdVideoCameraBack,
} from "react-icons/md";
import useModal from "antd/es/modal/useModal";
import { masterToOptions } from "../../../../utils/functions/misc/masterToOptions";
import { masterAtom } from "../../../../atoms/masters.atom";
import { axios } from "../../../../api/axios";
import { ImageUploader } from "../../../../components/ui/image-uploader/image-uploader.component";
import { replacePoster, samePosterUrl } from "../../utils/posters";
import { TextAreaResize } from "../../../../components/ui/textarea-resize/textarea-resize.component";
import { Season } from "../../../../models/season.model";
import {
  episodeDurationParts,
  episodeDurationValue,
} from "../../utils/duration";
import { StatePair } from "../../../../types/SetState";
import { colors } from "../../../../theme/colors";
import {
  Field,
  FieldGrid,
  HelperRow,
  Section,
  card,
} from "../../../films/components/section-card.component";
import { ProfileAutocomplete } from "../../../films/components/autocomplete.component";

const SYNOPSIS_LIMIT = 1500;

type NameOption = {
  value: string;
  label: string;
  logo?: string;
  id?: string;
  full_name?: string;
  custom_url?: string;
  type?: "professional" | "business";
};

export const StepFive = ({
  seasons,
  setSeasons,
  activeSeasonIndex,
  setActiveSeasonIndex,
  show = true,
  stepFiveTab,
  setStepFiveTab,
  activeEpisodeIndex,
  setActiveEpisodeIndex,
  professionalProfileSubject$,
}: {
  show?: boolean;
  nameOptions: NameOption[];
  professionalProfileSubject$: Subject<string>;
  stepFiveTab: 0 | 1 | 2;
  setStepFiveTab: (tab: 0 | 1 | 2) => void;
} & StatePair<"seasons", "setSeasons", Season[]> &
  StatePair<"activeSeasonIndex", "setActiveSeasonIndex", number> &
  StatePair<"activeEpisodeIndex", "setActiveEpisodeIndex", number>) => {
  const [modal, modalConfig] = useModal();
  const master = useAtomValue(masterAtom);
  const form = Form.useFormInstance();

  const [deleteEpisodeConfirmVisible, setDeleteEpisodeConfirmVisible] =
    useState(false);
  const [episodeToDelete, setEpisodeToDelete] = useState<number | null>(null);

  const [nameOptions, setNameOptions] = useState<NameOption[]>([]);
  const [nameModalType, setNameModalType] = useState<0 | 1 | 2 | 3 | null>(
    null
  );

  const [episodeDirectors, setEpisodeDirectors] = useState<
    {
      name: string;
      user?: string;
      biography: string;
      id?: string;
      type?: "professional" | "business";
      logo?: string;
    }[][][]
  >([]);
  const [episodeProducers, setEpisodeProducers] = useState<
    {
      name: string;
      user?: string;
      filmography: string;
      id?: string;
      type?: "professional" | "business";
      logo?: string;
    }[][][]
  >([]);
  const [episodeCast, setEpisodeCast] = useState<
    {
      name: string;
      user?: string;
      character: string;
      id?: string;
      type?: "professional" | "business";
      logo?: string;
    }[][][]
  >([]);
  const [episodeCrew, setEpisodeCrew] = useState<
    {
      name: string;
      user?: string;
      role: string;
      id?: string;
      type?: "professional" | "business";
      logo?: string;
    }[][][]
  >([]);

  const [copyFromPosition, setCopyFromPosition] = useState<[number, number]>([
    0, 0,
  ]);

  const personHasName = (people?: { name?: string }[]) =>
    (people ?? []).some((person) => (person?.name ?? "").trim());

  const cloneAsNewPeople = <T extends { id?: string }>(people?: T[]) => {
    if (!people) return people;
    return _.cloneDeep(people).map((person) => ({ ...person, id: v4() }));
  };

  const namedPeople = <T extends { name?: string }>(people?: T[]) =>
    (people ?? []).filter((person) => (person?.name ?? "").trim());

  const sameRole = (role: string | undefined, expected: string) =>
    (role ?? "").trim().toLowerCase() === expected;

  useEffect(() => {
    const fill = <T extends { name?: string }>(
      current: T[][][],
      pick: (episode: {
        cast?: T[];
        episode_cast?: T[];
        crew?: { name?: string; role?: string }[];
        episode_crew?: { name?: string; role?: string }[];
      }) => T[]
    ) => {
      const next = _.cloneDeep(current);
      let changed = next.length < seasons.length;
      while (next.length < seasons.length) next.push([]);
      seasons.forEach((season, seasonIndex) => {
        if (next[seasonIndex].length < season.episodes.length) changed = true;
        while (next[seasonIndex].length < season.episodes.length) {
          next[seasonIndex].push([]);
        }
        season.episodes.forEach((episode, episodeIndex) => {
          const saved = pick(episode as any);
          const slot = next[seasonIndex][episodeIndex] ?? [];
          if (!personHasName(slot) && personHasName(saved)) {
            next[seasonIndex][episodeIndex] = _.cloneDeep(saved) as T[];
            changed = true;
          }
        });
      });
      return changed ? next : current;
    };

    setEpisodeDirectors((current) =>
      fill(current, (episode) => {
        const crew = episode.crew?.length ? episode.crew : episode.episode_crew ?? [];
        return crew.filter((member) => sameRole(member.role, "director")) as any;
      })
    );
    setEpisodeProducers((current) =>
      fill(current, (episode) => {
        const crew = episode.crew?.length ? episode.crew : episode.episode_crew ?? [];
        return crew.filter((member) => sameRole(member.role, "producer")) as any;
      })
    );
    setEpisodeCast((current) =>
      fill(current, (episode) => {
        const primary = (
          episode.cast?.length ? episode.cast : episode.episode_cast ?? []
        ) as { id?: string; character?: string }[];
        const savedCast = (episode.episode_cast ?? []) as {
          id?: string;
          character?: string;
        }[];
        const savedById = new Map(
          savedCast
            .filter((person) => person?.id)
            .map((person) => [person.id, person])
        );
        return primary.map((person) => {
          const previous = person?.id ? savedById.get(person.id) : undefined;
          if (
            previous &&
            !(person.character ?? "").trim() &&
            (previous.character ?? "").trim()
          ) {
            return { ...person, character: previous.character };
          }
          return person;
        }) as any;
      })
    );
    setEpisodeCrew((current) =>
      fill(current, (episode) => {
        const crew = episode.crew?.length ? episode.crew : episode.episode_crew ?? [];
        return crew.filter(
          (member) =>
            !sameRole(member.role, "director") && !sameRole(member.role, "producer")
        ) as any;
      })
    );
  }, [seasons]);

  useEffect(() => {
    setSeasons((currentSeasons) => {
      const nextSeasons = _.cloneDeep(currentSeasons);
      let changed = false;

      nextSeasons.forEach((season, seasonIndex) => {
        season.episodes.forEach((episode, episodeIndex) => {
          const directors = episodeDirectors[seasonIndex]?.[episodeIndex];
          const producers = episodeProducers[seasonIndex]?.[episodeIndex];
          const cast = episodeCast[seasonIndex]?.[episodeIndex];
          const crew = episodeCrew[seasonIndex]?.[episodeIndex];
          if (!directors || !producers || !cast || !crew) return;

          const savedPeople = [
            ...(episode.cast ?? []),
            ...((episode as { episode_cast?: { name?: string }[] }).episode_cast ?? []),
            ...(episode.crew ?? []),
            ...((episode as { episode_crew?: { name?: string }[] }).episode_crew ?? []),
          ];
          const editedPeople = [...cast, ...crew, ...directors, ...producers];
          if (!personHasName(editedPeople) && personHasName(savedPeople)) return;

          const nextCast = namedPeople(cast);
          const nextCrew = [
            ...namedPeople(crew),
            ...namedPeople(directors).map((director) => ({
              ...director,
              role: "director",
            })),
            ...namedPeople(producers).map((producer) => ({
              ...producer,
              role: "producer",
            })),
          ];
          if (
            JSON.stringify(episode.cast ?? []) === JSON.stringify(nextCast) &&
            JSON.stringify(episode.crew ?? []) === JSON.stringify(nextCrew)
          ) {
            return;
          }
          episode.cast = nextCast as any;
          episode.crew = nextCrew as any;
          episode.modified = true;
          season.modified = true;
          changed = true;
        });
      });

      return changed ? nextSeasons : currentSeasons;
    });
  }, [episodeDirectors, episodeProducers, episodeCast, episodeCrew]);

  useEffect(() => {
    // Character inputs are form fields, so a saved name stays hidden until it
    // is copied into the form. That empty field is what blocked Season 2 and
    // made Season 1 look like its characters had been deleted.
    const updates: { name: string; value: string; errors: string[] }[] = [];
    episodeCast.forEach((seasonCast) => {
      seasonCast?.forEach((members) => {
        members?.forEach((member) => {
          if (!member?.id) return;
          const nextValue = member.character ?? "";
          if (!nextValue.trim()) return;
          const fieldName = `character.${member.id}`;
          if (form.getFieldValue(fieldName) === nextValue) return;
          updates.push({ name: fieldName, value: nextValue, errors: [] });
        });
      });
    });
    if (updates.length) form.setFields(updates);
  }, [episodeCast, form]);

  useEffect(() => {
    const listener = professionalProfileSubject$
      .pipe(debounceTime(500))
      .subscribe((query) => {
        axios
          .get<
            {
              full_name: string;
              business_name?: string;
              custom_url: string;
              id: string;
              logo: string;
              profile_type?: "professional" | "business";
            }[]
          >(
            `/client/profile/search?q=${query}&type=${nameModalType === 1 || nameModalType === 3
              ? "all"
              : "professional"
            }`
          )
          .then((response) => {
            setNameOptions(
              response.data.map((res) => ({
                value: res.full_name ?? res.business_name,
                label: (
                  <div style={{ display: "flex", gap: "1rem" }}>
                    <img
                      src={res.logo ?? "/images/ui/avatar.png"}
                      alt="Profile Picture"
                      style={{
                        width: "2rem",
                        height: "2rem",
                        borderRadius: "50%",
                      }}
                    />
                    {res.full_name ?? res.business_name}
                  </div>
                ) as any,
                ...res,
                full_name: res.full_name ?? res.business_name,
                type: res.profile_type,
              }))
            );
          });
      });

    return () => listener.unsubscribe();
  }, [nameModalType]);

  const activeSeason = seasons[activeSeasonIndex] ?? ({} as Season);
  const activeEpisode = activeSeason?.episodes?.[activeEpisodeIndex];
  const episodeLabel =
    activeEpisode?.title || `Episode ${activeEpisodeIndex + 1}`;
  const synopsisCount = activeEpisode?.synopsis?.length ?? 0;

  const mutateActiveEpisode = (mutator: (ep: any) => void) => {
    const next = _.cloneDeep(seasons);
    const ep = next[activeSeasonIndex].episodes[activeEpisodeIndex];
    mutator(ep);
    ep.modified = true;
    next[activeSeasonIndex].modified = true;
    setSeasons(next);
  };

  const durationStr = episodeDurationParts(activeEpisode?.duration);

  const updateEpisodeVerticalAt = (type: 0 | 1 | 2) => ({
    url: (activeEpisode?.vertical_poster ?? [])
      .filter((p) => p.type === type)
      .map((p) => p.url ?? ""),
    onRemove: (file: UploadFile) => {
      mutateActiveEpisode((ep) => {
        ep.vertical_poster = (ep.vertical_poster ?? []).filter(
          (poster: { type?: number; url?: string | null }) =>
            !(poster.type === type && samePosterUrl(poster.url, file.url))
        );
      });
    },
    onComplete: (urls: UploadFile[]) => {
      mutateActiveEpisode((ep) => {
        ep.vertical_poster = replacePoster(ep.vertical_poster, type, urls.at(-1));
      });
    },
  });

  const updateEpisodeHorizontalAt = (type: 0 | 1 | 2) => ({
    url: (activeEpisode?.horizontal_poster ?? [])
      .filter((p) => p.type === type)
      .map((p) => p.url ?? ""),
    onRemove: (file: UploadFile) => {
      mutateActiveEpisode((ep) => {
        ep.horizontal_poster = (ep.horizontal_poster ?? []).filter(
          (poster: { type?: number; url?: string | null }) =>
            !(poster.type === type && samePosterUrl(poster.url, file.url))
        );
      });
    },
    onComplete: (urls: UploadFile[]) => {
      mutateActiveEpisode((ep) => {
        ep.horizontal_poster = replacePoster(ep.horizontal_poster, type, urls.at(-1));
      });
    },
  });

  const addEpisode = () => {
    const id = v4();
    const next = [...seasons];
    next[activeSeasonIndex] = {
      ...next[activeSeasonIndex],
      modified: true,
      episodes: [
        ...next[activeSeasonIndex].episodes,
        {
          title: "",
          duration: "0:0",
          episode_number: next[activeSeasonIndex].episodes.length + 1,
          release_date: "",
          cast: [],
          crew: [],
          images: [],
          vertical_poster: [],
          horizontal_poster: [],
          synopsis: "",
          shooting_format: "",
          aspect_ratio: "",
          id,
          ratings: {
            censor: "",
            adultContent: "",
            language: "",
            violence: "",
            drugs: "",
            harmful: "",
            hateful: "",
          },
          awards: [],
        },
      ],
    };
    setSeasons(next);
    setActiveEpisodeIndex(next[activeSeasonIndex].episodes.length - 1);
  };

  const confirmDeleteEpisode = () => {
    if (episodeToDelete !== null) {
      const next = [...seasons];
      next[activeSeasonIndex] = {
        ...next[activeSeasonIndex],
        modified: true,
        episodes: next[activeSeasonIndex].episodes.filter(
          (_, i) => i !== episodeToDelete
        ),
      };
      setSeasons(next);
      if (activeEpisodeIndex === episodeToDelete) {
        setActiveEpisodeIndex(Math.max(0, episodeToDelete - 1));
      } else if (activeEpisodeIndex > episodeToDelete) {
        setActiveEpisodeIndex(activeEpisodeIndex - 1);
      }
    }
    setDeleteEpisodeConfirmVisible(false);
    setEpisodeToDelete(null);
  };

  return (
    <Wrapper style={{ display: show ? "flex" : "none" }}>
      {modalConfig}

      <EpisodeBar>
        <EpisodeBarHead>
          <EpisodeBarTitle>Episodes</EpisodeBarTitle>
          <EpisodeBarHint>
            Pick a season, then switch between its episodes.
          </EpisodeBarHint>
        </EpisodeBarHead>
        <SeasonPickerRow>
          <SeasonPickerLabel>Season</SeasonPickerLabel>
          <Select
            size="large"
            style={{ flex: 1, minWidth: 0, maxWidth: 28 + "rem" }}
            placeholder="Select season"
            value={activeSeasonIndex}
            onSelect={(i) => {
              setActiveSeasonIndex(i as number);
              setActiveEpisodeIndex(0);
            }}
            options={seasons.map((season, i) => ({
              label: season.season_title || `Season ${i + 1}`,
              value: i,
            }))}
          />
        </SeasonPickerRow>
        <EpisodeChips>
          {activeSeason?.episodes?.map((episode, index) => (
            <EpisodeChip
              key={episode.id ?? `episode-${index}`}
              $active={activeEpisodeIndex === index}
              onClick={() => setActiveEpisodeIndex(index)}
            >
              <ChipNumber>{index + 1}</ChipNumber>
              <ChipLabel>{episode.title || `Episode ${index + 1}`}</ChipLabel>
              {activeSeason.episodes.length > 1 ? (
                <ChipRemove
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    setEpisodeToDelete(index);
                    setDeleteEpisodeConfirmVisible(true);
                  }}
                  aria-label="Remove episode"
                >
                  <MdClose />
                </ChipRemove>
              ) : null}
            </EpisodeChip>
          ))}
          <AddEpisodeChip type="button" onClick={addEpisode}>
            <MdAdd />
            <span>Add episode</span>
          </AddEpisodeChip>
        </EpisodeChips>
      </EpisodeBar>

      <SegmentedTabs role="tablist">
        <SegmentedTab
          type="button"
          role="tab"
          $active={stepFiveTab === 0}
          aria-selected={stepFiveTab === 0}
          onClick={() => setStepFiveTab(0)}
        >
          <MdInfoOutline />
          Episode Details
        </SegmentedTab>
        <SegmentedTab
          type="button"
          role="tab"
          $active={stepFiveTab === 1}
          aria-selected={stepFiveTab === 1}
          onClick={() => setStepFiveTab(1)}
        >
          <MdImage />
          Posters
        </SegmentedTab>
        <SegmentedTab
          type="button"
          role="tab"
          $active={stepFiveTab === 2}
          aria-selected={stepFiveTab === 2}
          onClick={() => setStepFiveTab(2)}
        >
          <MdGroups />
          Cast & Crew
        </SegmentedTab>
      </SegmentedTabs>

      {stepFiveTab === 0 ? (
        <Section
          icon={<MdInfoOutline />}
          title={`${episodeLabel} · Details`}
          subtitle="Title, runtime and basic info for this episode."
        >
          <FieldGrid $cols={3}>
            <Field label="Episode Name" icon={<MdTitle />}>
              <Input
                size="large"
                placeholder={`Episode ${activeEpisodeIndex + 1}`}
                value={activeEpisode?.title ?? ""}
                onChange={(e) =>
                  mutateActiveEpisode((ep) => {
                    ep.title = e.target.value;
                  })
                }
              />
            </Field>
            <Field
              label="Duration"
              icon={<MdAccessTime />}
              required
              helper="Total runtime in minutes : seconds."
            >
              <DurationRow>
                <Input
                  type="number"
                  size="large"
                  placeholder="mm"
                  min="0"
                  value={durationStr.mm}
                  onChange={(e) => {
                    const minutes = +e.target.value;
                    mutateActiveEpisode((ep) => {
                      ep.duration = episodeDurationValue(minutes, +durationStr.ss);
                    });
                  }}
                />
                <span className="duration-separator">:</span>
                <Input
                  type="number"
                  size="large"
                  placeholder="ss"
                  min="0"
                  max={60}
                  value={durationStr.ss}
                  onChange={(e) => {
                    const seconds = +e.target.value;
                    mutateActiveEpisode((ep) => {
                      ep.duration = episodeDurationValue(+durationStr.mm, seconds);
                    });
                  }}
                />
              </DurationRow>
            </Field>
            <Field
              label="Tagline"
              icon={<MdFormatQuote />}
              helper="A short one-liner for this episode."
            >
              <Input
                size="large"
                placeholder="Optional tagline"
                maxLength={50}
                value={activeEpisode?.tagline ?? ""}
                onChange={(e) =>
                  mutateActiveEpisode((ep) => {
                    ep.tagline = e.target.value;
                  })
                }
              />
            </Field>
            <Field
              label="Date of Completion"
              icon={<MdEvent />}
              helper="When did this episode wrap?"
            >
              <DatePicker
                size="large"
                style={{ width: "100%" }}
                format="YYYY-MM-DD"
                placeholder="Select date"
                value={(activeEpisode?.release_date as any) ?? undefined}
                onChange={(date) =>
                  mutateActiveEpisode((ep) => {
                    ep.release_date = date;
                  })
                }
              />
            </Field>
            <Field
              label="Shooting Format"
              icon={<MdVideoCameraBack />}
              required
              helper="The capture format used during production."
            >
              <Select
                size="large"
                showSearch
                allowClear
                placeholder="Select format"
                options={masterToOptions("shooting_format", master)}
                value={activeEpisode?.shooting_format ?? undefined}
                onSelect={(shootingFormat) =>
                  mutateActiveEpisode((ep) => {
                    ep.shooting_format = shootingFormat as string;
                  })
                }
              />
            </Field>
            <Field
              label="Original Aspect Ratio"
              icon={<MdAspectRatio />}
              required
              helper="The framing for this episode."
            >
              <Select
                size="large"
                showSearch
                allowClear
                placeholder="Select aspect ratio"
                options={masterToOptions("aspect_ratio", master)}
                value={activeEpisode?.aspect_ratio ?? undefined}
                onSelect={(aspectRatio) =>
                  mutateActiveEpisode((ep) => {
                    ep.aspect_ratio = aspectRatio as string;
                  })
                }
              />
            </Field>
            <Field
              label="Synopsis"
              icon={<MdSubject />}
              required
              helper={
                <HelperRow>
                  <span>What happens in this episode?</span>
                  <span>
                    {synopsisCount} / {SYNOPSIS_LIMIT}
                  </span>
                </HelperRow>
              }
              fullWidth
            >
              <TextAreaResize
                placeholder="Tell us what this episode is about…"
                rows={4}
                maxLength={SYNOPSIS_LIMIT}
                value={activeEpisode?.synopsis ?? ""}
                onChange={(value) =>
                  mutateActiveEpisode((ep) => {
                    ep.synopsis = value.toString();
                  })
                }
              />
            </Field>
          </FieldGrid>
        </Section>
      ) : null}

      {stepFiveTab === 1 ? (
        <Section
          icon={<MdImage />}
          title={`${episodeLabel} · Posters`}
          subtitle="Artwork specific to this episode. JPG or PNG, up to 10MB each."
        >
          <SubHead>Vertical posters</SubHead>
          <PosterGrid>
            <UploadField
              label="Vertical Poster"
              aspect="3:4"
              help="Recommended for thumbnails."
              required
            >
              <ImageUploader
                outputAsJpeg
                maxCount={2}
                aspect={3 / 4}
                name="vertical_poster"
                action=""
                placeholder="Click or drag"
                hint="3:4 · max 2"
                {...updateEpisodeVerticalAt(0)}
              />
            </UploadField>
            <UploadField label="Vertical Poster" aspect="4:5" help="Optional.">
              <ImageUploader
                outputAsJpeg
                maxCount={2}
                aspect={4 / 5}
                name="vertical_poster"
                action=""
                placeholder="Click or drag"
                hint="4:5 · max 2"
                {...updateEpisodeVerticalAt(1)}
              />
            </UploadField>
            <UploadField label="Vertical Poster" aspect="1:1" help="Optional.">
              <ImageUploader
                outputAsJpeg
                maxCount={2}
                aspect={1 / 1}
                name="vertical_poster"
                action=""
                placeholder="Click or drag"
                hint="1:1 · max 2"
                {...updateEpisodeVerticalAt(2)}
              />
            </UploadField>
          </PosterGrid>

          <Divider />

          <SubHead>Horizontal posters</SubHead>
          <PosterGrid>
            <UploadField
              label="Horizontal Poster"
              aspect="16:9"
              help="Recommended for wide layouts."
              required
            >
              <ImageUploader
                outputAsJpeg
                maxCount={2}
                aspect={16 / 9}
                name="horizontal_poster"
                action=""
                placeholder="Click or drag"
                hint="16:9 · max 2"
                {...updateEpisodeHorizontalAt(0)}
              />
            </UploadField>
            <UploadField
              label="Horizontal Poster"
              aspect="4:3"
              help="Optional."
            >
              <ImageUploader
                outputAsJpeg
                maxCount={2}
                aspect={4 / 3}
                name="horizontal_poster"
                action=""
                placeholder="Click or drag"
                hint="4:3 · max 2"
                {...updateEpisodeHorizontalAt(1)}
              />
            </UploadField>
            <UploadField
              label="Horizontal Poster"
              aspect="2.39:1"
              help="Optional."
            >
              <ImageUploader
                outputAsJpeg
                maxCount={2}
                aspect={2.39 / 1}
                name="horizontal_poster"
                action=""
                placeholder="Click or drag"
                hint="2.39:1 · max 2"
                {...updateEpisodeHorizontalAt(2)}
              />
            </UploadField>
          </PosterGrid>
        </Section>
      ) : null}

      {stepFiveTab === 2 ? (
        <>
          <CopyCard>
            <CopyHead>
              <CopyIcon>
                <MdContentCopy />
              </CopyIcon>
              <CopyText>
                <CopyTitle>Reuse cast & crew</CopyTitle>
                <CopyHint>
                  Quickly copy details from another episode you've already
                  filled out.
                </CopyHint>
              </CopyText>
            </CopyHead>
            <CopyControls>
              <Select
                size="large"
                style={{ flex: 1, minWidth: 0 }}
                placeholder="Copy details from"
                options={seasons.map((season, i) => ({
                  label: season.season_title || `Season ${i + 1}`,
                  title: season.season_title || `Season ${i + 1}`,
                  options: season.episodes?.map((episode, j) => ({
                    label: episode.title || `Episode ${j + 1}`,
                    value: `${i}-${j}`,
                    position: [i, j] as [number, number],
                    disabled:
                      i === activeSeasonIndex && j === activeEpisodeIndex,
                  })),
                }))}
                onSelect={(_value, option: any) => {
                  if (option?.position) {
                    setCopyFromPosition(option.position);
                  }
                }}
              />
              <Button
                size="large"
                type="primary"
                icon={<MdContentCopy />}
                onClick={async () => {
                  const newEpisodeDirectors = _.cloneDeep(episodeDirectors);
                  const newEpisodeProducers = _.cloneDeep(episodeProducers);
                  const newEpisodeCast = _.cloneDeep(episodeCast);
                  const newEpisodeCrew = _.cloneDeep(episodeCrew);

                  const [copySeason, copyEpisode] = copyFromPosition;

                  if (
                    newEpisodeDirectors[activeSeasonIndex]
                      ?.at(activeEpisodeIndex)
                      ?.filter(({ name }) => !!name).length ||
                    newEpisodeProducers[activeSeasonIndex]
                      ?.at(activeEpisodeIndex)
                      ?.filter(({ name }) => !!name).length ||
                    newEpisodeCast[activeSeasonIndex]
                      ?.at(activeEpisodeIndex)
                      ?.filter(({ name }) => !!name).length ||
                    newEpisodeCrew[activeSeasonIndex]
                      ?.at(activeEpisodeIndex)
                      ?.filter(({ name }) => !!name).length
                  ) {
                    const confirmed = await modal.confirm({
                      title: `Copy from Season ${copySeason + 1
                        } Episode ${copyEpisode + 1}`,
                      content:
                        "Are you sure you want to copy cast and crew details from this episode? Current data will be overwritten.",
                      okText: "Copy",
                    });

                    if (!confirmed) return;
                  }

                  newEpisodeDirectors[activeSeasonIndex][activeEpisodeIndex] =
                    cloneAsNewPeople(
                      newEpisodeDirectors[copySeason][copyEpisode]
                    ) as (typeof newEpisodeDirectors)[number][number];
                  newEpisodeProducers[activeSeasonIndex][activeEpisodeIndex] =
                    cloneAsNewPeople(
                      newEpisodeProducers[copySeason][copyEpisode]
                    ) as (typeof newEpisodeProducers)[number][number];
                  newEpisodeCast[activeSeasonIndex][activeEpisodeIndex] =
                    cloneAsNewPeople(
                      newEpisodeCast[copySeason][copyEpisode]
                    ) as (typeof newEpisodeCast)[number][number];
                  newEpisodeCrew[activeSeasonIndex][activeEpisodeIndex] =
                    cloneAsNewPeople(
                      newEpisodeCrew[copySeason][copyEpisode]
                    ) as (typeof newEpisodeCrew)[number][number];

                  setEpisodeDirectors(newEpisodeDirectors);
                  setEpisodeProducers(newEpisodeProducers);
                  setEpisodeCast(newEpisodeCast);
                  setEpisodeCrew(newEpisodeCrew);
                }}
              >
                Copy
              </Button>
            </CopyControls>
          </CopyCard>

          <Section
            icon={<MdMovieCreation />}
            title="Directors"
            subtitle="Everyone credited with directing this episode."
          >
            <PeopleList
              count={
                episodeDirectors[activeSeasonIndex]?.at(activeEpisodeIndex)
                  ?.length ?? 0
              }
              emptyTitle="No directors added yet"
              emptyHint="Add the people who directed this episode."
              addLabel="Add director"
              onAdd={() => {
                const next = _.cloneDeep(episodeDirectors);
                next[activeSeasonIndex][activeEpisodeIndex] = [
                  ...(next[activeSeasonIndex][activeEpisodeIndex] ?? []),
                  { name: "", biography: "", id: v4() },
                ];
                setEpisodeDirectors(next);
              }}
            >
              {(episodeDirectors[activeSeasonIndex]?.at(activeEpisodeIndex) ??
                []).map((director, index) => (
                <PersonRow
                  key={director.id ?? `director-${index}`}
                  logo={director.logo}
                  canRemove={
                    (episodeDirectors[activeSeasonIndex]?.at(
                      activeEpisodeIndex
                    )?.length ?? 0) > 1
                  }
                  onRemove={() => {
                    const next = _.cloneDeep(episodeDirectors);
                    next[activeSeasonIndex][activeEpisodeIndex].splice(
                      index,
                      1
                    );
                    setEpisodeDirectors(next);
                  }}
                >
                  <ProfileAutocomplete
                    targetNameModalType={0}
                    name={director.name}
                    index={index}
                    nameOptions={nameOptions}
                    setNameModalType={setNameModalType}
                    subject$={professionalProfileSubject$}
                    array={
                      episodeDirectors[activeSeasonIndex]?.at(
                        activeEpisodeIndex
                      ) ?? []
                    }
                    setArray={
                      ((directors: any[]) => {
                        const next = _.cloneDeep(episodeDirectors);
                        next[activeSeasonIndex][activeEpisodeIndex] = directors;
                        setEpisodeDirectors(next);
                      }) as any
                    }
                    dataProperty="biography"
                  />
                </PersonRow>
              ))}
            </PeopleList>
          </Section>

          <Section
            icon={<MdBusinessCenter />}
            title="Producers"
            subtitle="Everyone credited with producing this episode."
          >
            <PeopleList
              count={
                episodeProducers[activeSeasonIndex]?.at(activeEpisodeIndex)
                  ?.length ?? 0
              }
              emptyTitle="No producers added yet"
              emptyHint="Add the producers backing this episode."
              addLabel="Add producer"
              onAdd={() => {
                const next = _.cloneDeep(episodeProducers);
                next[activeSeasonIndex][activeEpisodeIndex] = [
                  ...(next[activeSeasonIndex][activeEpisodeIndex] ?? []),
                  { name: "", filmography: "", id: v4() },
                ];
                setEpisodeProducers(next);
              }}
            >
              {(episodeProducers[activeSeasonIndex]?.at(activeEpisodeIndex) ??
                []).map((producer, index) => (
                <PersonRow
                  key={producer.id ?? `producer-${index}`}
                  logo={producer.logo}
                  canRemove={
                    (episodeProducers[activeSeasonIndex]?.at(
                      activeEpisodeIndex
                    )?.length ?? 0) > 1
                  }
                  onRemove={() => {
                    const next = _.cloneDeep(episodeProducers);
                    next[activeSeasonIndex][activeEpisodeIndex].splice(
                      index,
                      1
                    );
                    setEpisodeProducers(next);
                  }}
                >
                  <ProfileAutocomplete
                    targetNameModalType={1}
                    name={producer.name}
                    index={index}
                    nameOptions={nameOptions}
                    setNameModalType={setNameModalType}
                    subject$={professionalProfileSubject$}
                    array={
                      episodeProducers[activeSeasonIndex]?.at(
                        activeEpisodeIndex
                      ) ?? []
                    }
                    setArray={
                      ((producers: any[]) => {
                        const next = _.cloneDeep(episodeProducers);
                        next[activeSeasonIndex][activeEpisodeIndex] = producers;
                        setEpisodeProducers(next);
                      }) as any
                    }
                    dataProperty="filmography"
                  />
                </PersonRow>
              ))}
            </PeopleList>
          </Section>

          <Section
            icon={<MdGroups />}
            title="Cast"
            subtitle="Actors and the characters they play."
          >
            <PeopleList
              count={
                episodeCast[activeSeasonIndex]?.at(activeEpisodeIndex)
                  ?.length ?? 0
              }
              emptyTitle="No cast added yet"
              emptyHint="Add the actors and their characters."
              addLabel="Add cast member"
              onAdd={() => {
                const next = _.cloneDeep(episodeCast);
                if (!next[activeSeasonIndex]) next[activeSeasonIndex] = [];
                next[activeSeasonIndex][activeEpisodeIndex] = [
                  ...(next[activeSeasonIndex][activeEpisodeIndex] ?? []),
                  { name: "", character: "", id: v4() },
                ];
                setEpisodeCast(next);
              }}
            >
              {(episodeCast[activeSeasonIndex]?.at(activeEpisodeIndex) ?? [])
                .map((member, index) => (
                  <PersonRow
                    key={member.id ?? `cast-${index}`}
                    logo={member.logo}
                    onRemove={() => {
                      const next = _.cloneDeep(episodeCast);
                      next[activeSeasonIndex][activeEpisodeIndex].splice(
                        index,
                        1
                      );
                      setEpisodeCast(next);
                    }}
                  >
                    <FieldStack>
                      <ProfileAutocomplete
                        targetNameModalType={2}
                        name={member.name}
                        index={index}
                        nameOptions={nameOptions}
                        setNameModalType={setNameModalType}
                        subject$={professionalProfileSubject$}
                        array={
                          episodeCast[activeSeasonIndex]?.at(
                            activeEpisodeIndex
                          ) ?? []
                        }
                        setArray={
                          ((cast: any[]) => {
                            const next = _.cloneDeep(episodeCast);
                            next[activeSeasonIndex][activeEpisodeIndex] = cast;
                            setEpisodeCast(next);
                          }) as any
                        }
                        dataProperty="character"
                      />
                      <Form.Item
                        name={`character.${member.id}`}
                        initialValue={member.character ?? ""}
                        rules={[
                          {
                            required: true,
                            message: "Please enter a character name!",
                          },
                        ]}
                        style={{ marginBottom: 0 }}
                      >
                        <Input
                          placeholder="Character name"
                          value={member.character}
                          onChange={(evt) => {
                            const next = _.cloneDeep(episodeCast);
                            next[activeSeasonIndex][activeEpisodeIndex][
                              index
                            ].character = evt.target.value;
                            setEpisodeCast(next);
                          }}
                        />
                      </Form.Item>
                    </FieldStack>
                  </PersonRow>
                ))}
            </PeopleList>
          </Section>

          <Section
            icon={<MdHandyman />}
            title="Crew"
            subtitle="The people behind the scenes and their roles."
          >
            <PeopleList
              count={
                episodeCrew[activeSeasonIndex]?.at(activeEpisodeIndex)
                  ?.length ?? 0
              }
              emptyTitle="No crew added yet"
              emptyHint="Add the people behind the camera."
              addLabel="Add crew member"
              onAdd={() => {
                const next = _.cloneDeep(episodeCrew);
                if (!next[activeSeasonIndex]) next[activeSeasonIndex] = [];
                next[activeSeasonIndex][activeEpisodeIndex] = [
                  ...(next[activeSeasonIndex][activeEpisodeIndex] ?? []),
                  { name: "", role: "", id: v4() },
                ];
                setEpisodeCrew(next);
              }}
            >
              {(episodeCrew[activeSeasonIndex]?.at(activeEpisodeIndex) ?? [])
                .map((member, index) => (
                  <PersonRow
                    key={member.id ?? `crew-${index}`}
                    logo={member.logo}
                    onRemove={() => {
                      const next = _.cloneDeep(episodeCrew);
                      next[activeSeasonIndex][activeEpisodeIndex].splice(
                        index,
                        1
                      );
                      setEpisodeCrew(next);
                    }}
                  >
                    <FieldStack>
                      <ProfileAutocomplete
                        targetNameModalType={3}
                        name={member.name}
                        index={index}
                        nameOptions={nameOptions}
                        setNameModalType={setNameModalType}
                        subject$={professionalProfileSubject$}
                        array={
                          episodeCrew[activeSeasonIndex]?.at(
                            activeEpisodeIndex
                          ) ?? []
                        }
                        setArray={
                          ((crew: any[]) => {
                            const next = _.cloneDeep(episodeCrew);
                            next[activeSeasonIndex][activeEpisodeIndex] = crew;
                            setEpisodeCrew(next);
                          }) as any
                        }
                        dataProperty="role"
                      />
                      <Form.Item
                        name={`crew-role.${member.id}`}
                        initialValue={member.role}
                        style={{ marginBottom: 0 }}
                      >
                        <Select
                          options={masterToOptions("crew_role", master)}
                          placeholder="Select role"
                          value={member.role}
                          showSearch
                          style={{ width: "100%" }}
                          onSelect={(role) => {
                            const next = _.cloneDeep(episodeCrew);
                            next[activeSeasonIndex][activeEpisodeIndex][
                              index
                            ].role = role as string;
                            setEpisodeCrew(next);
                          }}
                        />
                      </Form.Item>
                    </FieldStack>
                  </PersonRow>
                ))}
            </PeopleList>
          </Section>
        </>
      ) : null}

      <Modal
        title="Delete this episode?"
        open={deleteEpisodeConfirmVisible}
        onOk={confirmDeleteEpisode}
        onCancel={() => {
          setDeleteEpisodeConfirmVisible(false);
          setEpisodeToDelete(null);
        }}
        okText="Delete"
        cancelText="Cancel"
        okButtonProps={{ danger: true }}
      >
        <p>
          Are you sure you want to delete Episode{" "}
          {episodeToDelete !== null ? episodeToDelete + 1 : ""}? This action
          cannot be undone.
        </p>
      </Modal>
    </Wrapper>
  );
};

const UploadField = ({
  label,
  aspect,
  help,
  required,
  error,
  children,
}: {
  label: string;
  aspect?: string;
  help?: string;
  required?: boolean;
  error?: string;
  children: ReactNode;
}) => (
  <UploadFieldShell>
    <FieldHead>
      <FieldLabel>
        <MdPhotoLibrary />
        <span>
          {label}
          {required ? <RequiredMark>*</RequiredMark> : null}
        </span>
      </FieldLabel>
      {aspect ? <AspectChip>{aspect}</AspectChip> : null}
    </FieldHead>
    <UploadBox>{children}</UploadBox>
    {help ? <Helper>{help}</Helper> : null}
    {error ? <ErrorText>{error}</ErrorText> : null}
  </UploadFieldShell>
);

const PeopleList = ({
  count,
  emptyTitle,
  emptyHint,
  addLabel,
  onAdd,
  children,
}: {
  count: number;
  emptyTitle: string;
  emptyHint: string;
  addLabel: string;
  onAdd: () => void;
  children: ReactNode;
}) => (
  <ListShell>
    {count === 0 ? (
      <EmptyState>
        <EmptyIcon>
          <MdPersonOutline />
        </EmptyIcon>
        <EmptyTitle>{emptyTitle}</EmptyTitle>
        <EmptyHint>{emptyHint}</EmptyHint>
      </EmptyState>
    ) : (
      <RowList>{children}</RowList>
    )}
    <AddButton type="dashed" block icon={<MdAdd />} onClick={onAdd}>
      {addLabel}
    </AddButton>
  </ListShell>
);

const PersonRow = ({
  logo,
  onRemove,
  canRemove = true,
  children,
}: {
  logo?: string;
  onRemove: () => void;
  canRemove?: boolean;
  children: ReactNode;
}) => (
  <RowShell>
    <Avatar src={logo || "/images/ui/avatar.png"} alt="" />
    <RowBody>{children}</RowBody>
    {canRemove ? (
      <RemoveBtn type="button" onClick={onRemove} aria-label="Remove">
        <MdClose />
      </RemoveBtn>
    ) : (
      <RemoveSpacer />
    )}
  </RowShell>
);

const Wrapper = styled.div`
  width: 100%;
  flex-direction: column;
  gap: 1.6rem;

  &[style*="flex"] {
    display: flex !important;
  }
`;

const EpisodeBar = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.2rem;
  padding: 1.6rem;
  background-color: ${card.bg};
  border: 1px solid ${card.border};
  border-radius: 1.2rem;
  box-shadow: ${card.shadow};
`;

const EpisodeBarHead = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.2rem;
`;

const EpisodeBarTitle = styled.h3`
  margin: 0;
  font-size: 1.5rem;
  font-weight: 600;
  color: ${colors.text.hex};
  letter-spacing: -0.01em;
`;

const EpisodeBarHint = styled.span`
  font-size: 1.2rem;
  color: ${colors.text.hex};
  opacity: 0.55;
`;

const SeasonPickerRow = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  flex-wrap: wrap;
`;

const SeasonPickerLabel = styled.span`
  font-size: 1.25rem;
  font-weight: 500;
  color: ${colors.text.hex};
  opacity: 0.7;
`;

const EpisodeChips = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.7rem;
`;

const EpisodeChip = styled.button<{ $active?: boolean }>`
  position: relative;
  display: inline-flex;
  align-items: center;
  gap: 0.6rem;
  padding: 0.55rem 1.1rem 0.55rem 0.6rem;
  background: ${({ $active }) => ($active ? colors.primary.hex : card.innerBg)};
  color: ${({ $active }) =>
    $active ? colors.white.hex : colors.text.hex};
  border: 1px solid
    ${({ $active }) => ($active ? colors.primary.hex : card.border)};
  border-radius: 999px;
  cursor: pointer;
  font-size: 1.25rem;
  font-weight: 500;
  letter-spacing: -0.005em;
  transition: background 0.15s, color 0.15s, border-color 0.15s, transform 0.1s;

  &:hover {
    border-color: ${colors.primary.hex};
  }

  &:active {
    transform: translateY(0.05rem);
  }
`;

const ChipNumber = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 2.2rem;
  height: 2.2rem;
  border-radius: 50%;
  background: ${card.bg};
  color: ${colors.primary.hex};
  font-size: 1.2rem;
  font-weight: 600;
  font-variant-numeric: tabular-nums;
`;

const ChipLabel = styled.span`
  font-size: 1.25rem;
  font-weight: 500;
  max-width: 16rem;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const ChipRemove = styled.button`
  width: 2rem;
  height: 2rem;
  border-radius: 50%;
  border: none;
  background: ${card.bg};
  color: ${colors.error.hex};
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  font-size: 1.3rem;
  opacity: 0.7;
  padding: 0;
  transition: background 0.15s, opacity 0.15s;

  &:hover {
    background: rgba(239, 35, 60, 0.12);
    opacity: 1;
  }
`;

const AddEpisodeChip = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.55rem 1.2rem;
  background: transparent;
  color: ${colors.primary.hex};
  border: 1.5px dashed ${card.border};
  border-radius: 999px;
  cursor: pointer;
  font-size: 1.25rem;
  font-weight: 500;
  transition: background 0.15s, border-color 0.15s, color 0.15s;

  & svg {
    font-size: 1.5rem;
  }

  &:hover {
    border-color: ${colors.primary.hex};
    background: ${card.accentSoft};
  }
`;

const SegmentedTabs = styled.div`
  display: inline-flex;
  padding: 0.4rem;
  background-color: ${card.bg};
  border: 1px solid ${card.border};
  border-radius: 0.9rem;
  gap: 0.3rem;
  align-self: flex-start;
`;

const SegmentedTab = styled.button<{ $active?: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: 0.6rem;
  padding: 0.7rem 1.4rem;
  background: ${({ $active }) =>
    $active ? colors.primary.hex : "transparent"};
  color: ${({ $active }) =>
    $active ? colors.white.hex : colors.text.hex};
  border: none;
  border-radius: 0.6rem;
  cursor: pointer;
  font-size: 1.3rem;
  font-weight: ${({ $active }) => ($active ? 600 : 500)};
  letter-spacing: -0.005em;
  transition: background 0.15s, color 0.15s;

  & svg {
    font-size: 1.55rem;
    opacity: ${({ $active }) => ($active ? 1 : 0.7)};
  }

  &:hover {
    background: ${({ $active }) =>
      $active ? colors.primary.hex : card.accentSoft};
  }
`;

const DurationRow = styled.div`
  display: flex;
  align-items: center;
  gap: 0.8rem;
  width: 100%;
  min-width: 0;

  & .ant-input {
    width: 100%;
    text-align: center;
  }

  & .duration-separator {
    flex: 0 0 auto;
    font-size: 1.8rem;
    font-weight: 600;
    color: ${colors.text.hex};
    opacity: 0.4;
  }
`;

const SubHead = styled.div`
  font-size: 1.2rem;
  font-weight: 600;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: ${colors.text.hex};
  opacity: 0.55;
`;

const Divider = styled.div`
  height: 1px;
  background: ${card.divider};
  width: 100%;
  margin: 0.4rem 0;
`;

const PosterGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 1.4rem 1.6rem;
  width: 100%;
  container-type: inline-size;

  @container (max-width: 56rem) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  @container (max-width: 32rem) {
    grid-template-columns: 1fr;
  }
`;

const UploadFieldShell = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.6rem;
  min-width: 0;
  width: 100%;
`;

const FieldHead = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.8rem;
`;

const FieldLabel = styled.label`
  display: inline-flex;
  align-items: center;
  gap: 0.6rem;
  font-size: 1.3rem;
  font-weight: 500;
  color: ${colors.text.hex};
  line-height: 1.2;

  svg {
    flex: 0 0 auto;
    color: ${colors.primary.hex};
    font-size: 1.5rem;
    opacity: 0.85;
  }
`;

const RequiredMark = styled.span`
  color: ${colors.error.hex};
  margin-left: 0.2rem;
`;

const AspectChip = styled.span`
  display: inline-flex;
  align-items: center;
  padding: 0.1rem 0.7rem;
  font-size: 1.05rem;
  font-weight: 600;
  letter-spacing: 0.04em;
  color: ${colors.primary.hex};
  background: ${card.bg};
  border: 1px solid ${card.border};
  border-radius: 999px;
`;

const TILE_SIZE = "11rem";

const UploadBox = styled.div`
  display: block;
  width: 100%;

  & .ant-upload-wrapper,
  & .ant-upload-picture-card-wrapper {
    width: 100%;
  }

  & .ant-upload-list,
  & .ant-upload-list-picture-card {
    display: flex !important;
    flex-wrap: wrap;
    gap: 0.8rem;
    align-items: flex-start;
  }

  & .ant-upload-list-item-container,
  & .ant-upload-list-picture-card .ant-upload-list-item-container {
    width: ${TILE_SIZE} !important;
    height: ${TILE_SIZE} !important;
    margin: 0 !important;
    flex: 0 0 auto;
  }

  & .ant-upload-list-item,
  & .ant-upload-list-item-done {
    width: 100% !important;
    height: 100% !important;
    padding: 0 !important;
    border: 1px solid ${card.border} !important;
    border-radius: 0.7rem !important;
    overflow: hidden;
    background: ${card.innerBg} !important;
  }

  & .ant-upload-list-item::before {
    border-radius: 0.7rem;
  }

  & .ant-upload-list-item-thumbnail,
  & .ant-upload-list-item-image,
  & .ant-upload-list-item-info {
    width: 100% !important;
    height: 100% !important;
  }

  & .ant-upload-list-item-thumbnail img,
  & .ant-upload-list-item-image img {
    width: 100% !important;
    height: 100% !important;
    object-fit: cover !important;
    border-radius: 0;
  }

  & .ant-upload-list-item-actions {
    inset: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 0.4rem;
  }

  & .ant-upload-list-item-actions .anticon {
    color: #fff !important;
    font-size: 1.6rem;
  }

  & .ant-upload.ant-upload-select,
  & .ant-upload-select.ant-upload-select-picture-card {
    width: ${TILE_SIZE} !important;
    height: ${TILE_SIZE} !important;
    flex: 0 0 auto;
    background: ${card.innerBg} !important;
    border: 1.5px dashed ${card.border} !important;
    border-radius: 0.7rem !important;
    transition: border-color 0.15s, background 0.15s, color 0.15s;
    margin: 0 !important;
  }

  & .ant-upload.ant-upload-select:hover,
  & .ant-upload-select.ant-upload-select-picture-card:hover {
    border-color: ${colors.primary.hex} !important;
    background: ${card.accentSoft} !important;
  }

  & .ant-upload.ant-upload-select .ant-upload,
  & .ant-upload-select.ant-upload-select-picture-card .ant-upload {
    padding: 0.6rem !important;
    color: ${colors.text.hex};
    display: flex !important;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 0.3rem;
  }

  & .ant-upload.ant-upload-select .ant-upload > div,
  & .ant-upload-select.ant-upload-select-picture-card .ant-upload > div {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 0.25rem;
  }

  & .ant-upload.ant-upload-select .ant-upload > div > div,
  & .ant-upload-select.ant-upload-select-picture-card .ant-upload > div > div {
    margin-top: 0 !important;
    font-size: 1.15rem;
    font-weight: 500;
    line-height: 1.2;
    color: ${colors.text.hex};
  }

  & .ant-upload.ant-upload-select .anticon,
  & .ant-upload-select.ant-upload-select-picture-card .anticon {
    color: ${colors.primary.hex};
    font-size: 1.7rem;
  }
`;

const Helper = styled.span`
  font-size: 1.15rem;
  color: ${colors.text.hex};
  opacity: 0.55;
  line-height: 1.4;
`;

const ErrorText = styled.span`
  font-size: 1.15rem;
  color: ${colors.error.hex};
  line-height: 1.4;
`;

const CopyCard = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
  padding: 1.4rem 1.6rem;
  background-color: ${card.bg};
  border: 1px solid ${card.border};
  border-radius: 1rem;
  box-shadow: ${card.shadow};
`;

const CopyHead = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
`;

const CopyIcon = styled.div`
  flex: 0 0 auto;
  width: 3.6rem;
  height: 3.6rem;
  border-radius: 0.8rem;
  background: ${card.innerBg};
  border: 1px solid ${card.border};
  color: ${colors.primary.hex};
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 1.9rem;
`;

const CopyText = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.1rem;
  min-width: 0;
`;

const CopyTitle = styled.span`
  font-size: 1.4rem;
  font-weight: 600;
  color: ${colors.text.hex};
`;

const CopyHint = styled.span`
  font-size: 1.15rem;
  color: ${colors.text.hex};
  opacity: 0.6;
  line-height: 1.4;
`;

const CopyControls = styled.div`
  display: flex;
  align-items: stretch;
  gap: 0.8rem;
  flex-wrap: wrap;

  & > .ant-select {
    min-width: min(100%, 24rem);
    max-width: 100%;
  }

  & > .ant-btn {
    height: auto;
    border-radius: 0.7rem;
  }
`;

const ListShell = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
  width: 100%;
  min-width: 0;
`;

const RowList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.8rem;
`;

const RowShell = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 1rem 1.1rem;
  background-color: ${card.innerBg};
  border: 1px solid ${card.border};
  border-radius: 0.9rem;
  transition: border-color 0.15s, box-shadow 0.15s;

  &:hover {
    border-color: ${colors.primary.hex};
    box-shadow: 0 1px 6px rgba(32, 84, 150, 0.06);
  }

  & .ant-form-item {
    margin-bottom: 0;
  }

  & .ant-form-item-explain {
    font-size: 1.15rem;
    margin-top: 0.3rem;
  }
`;

const Avatar = styled.img`
  flex: 0 0 auto;
  width: 3.4rem;
  height: 3.4rem;
  border-radius: 50%;
  border: 2px solid ${card.innerBg};
  box-shadow: 0 0 0 1px ${card.border};
  object-fit: cover;
`;

const RowBody = styled.div`
  flex: 1;
  min-width: 0;
  display: flex;
  align-items: center;
  gap: 0.8rem;

  & .ant-select,
  & .ant-input,
  & .ant-input-affix-wrapper,
  & .ant-select-selector {
    width: 100% !important;
  }
`;

const FieldStack = styled.div`
  width: 100%;
  min-width: 0;
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 0.8rem;
  container-type: inline-size;

  @container (max-width: 26rem) {
    grid-template-columns: 1fr;
  }
`;

const RemoveSpacer = styled.div`
  flex: 0 0 auto;
  width: 2.8rem;
  height: 2.8rem;
`;

const RemoveBtn = styled.button`
  flex: 0 0 auto;
  width: 2.8rem;
  height: 2.8rem;
  border-radius: 50%;
  border: none;
  background: transparent;
  color: ${colors.text.hex};
  opacity: 0.55;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  font-size: 1.8rem;
  transition: background 0.15s, color 0.15s, opacity 0.15s;

  &:hover {
    background: rgba(239, 35, 60, 0.1);
    color: ${colors.error.hex};
    opacity: 1;
  }
`;

const AddButton = styled(Button)`
  height: 4rem;
  border-radius: 0.8rem;
  font-weight: 500;
  border-style: dashed;
  border-color: ${card.border};
  background: transparent;
  color: ${colors.primary.hex};

  &:hover {
    border-color: ${colors.primary.hex} !important;
    color: ${colors.primary.hex} !important;
    background: ${card.accentSoft} !important;
  }
`;

const EmptyState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 0.4rem;
  padding: 2.4rem 1.6rem;
  background-color: ${card.innerBg};
  border: 1px dashed ${card.border};
  border-radius: 0.9rem;
  text-align: center;
`;

const EmptyIcon = styled.div`
  width: 4.4rem;
  height: 4.4rem;
  border-radius: 50%;
  background: ${card.accentSoft};
  color: ${colors.primary.hex};
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 2.4rem;
  margin-bottom: 0.4rem;
`;

const EmptyTitle = styled.div`
  font-size: 1.35rem;
  font-weight: 600;
  color: ${colors.text.hex};
`;

const EmptyHint = styled.div`
  font-size: 1.15rem;
  color: ${colors.text.hex};
  opacity: 0.6;
  line-height: 1.4;
`;
